"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { FRIEDMAN_TEST_CALCULATOR_STRINGS } from "@/components/tools/friedman-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { formatRangeP } from "@/components/tools/stats-tests-more/range-p";
import { t } from "@/lib/i18n/interpolate";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { FriedmanResult } from "@/lib/tool-kit/stats-tests-more/friedman";

/** Results for the Friedman test calculator. */

type Strings = typeof FRIEDMAN_TEST_CALCULATOR_STRINGS;

const SMALL_DESIGN = 10;
const RANK_TABLE_LIMIT = 40;

export function FriedmanResults({
  r,
  names,
  alpha,
  strings,
  fmt,
}: {
  r: FriedmanResult;
  names: readonly string[];
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const reject = r.p < alpha;
  const apa = richTemplate(strings.apaTemplate, {
    df: fmt(r.df),
    n: fmt(r.n),
    chi: fixed(r.chiSquare, 2, fmt),
    p: apaP(r.p, fmt),
    w: fixed(r.kendallW, 2, fmt, false),
  });
  const terms = r.rankSums.map((sum) => `${n(sum)}²`).join(" + ");
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.chiSquare} value={n(r.chiSquare)} />
        <StatCard label={strings.df} value={fmt(r.df)} />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.kendallW} value={n(r.kendallW)} />
        <StatCard label={strings.subjects} value={fmt(r.n)} />
        <StatCard label={strings.conditions} value={fmt(r.k)} />
      </StatGrid>
      <Decision reject={reject}>
        {t(reject ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      {r.n < SMALL_DESIGN ? (
        <p className={HINT}>{strings.smallSample}</p>
      ) : null}
      {r.k === 2 ? <p className={HINT}>{strings.twoConditions}</p> : null}
      <h3 className={WIDGET_H3}>{strings.summaryHeading}</h3>
      <ResultTable
        head={[
          strings.condition,
          strings.median,
          strings.rankSum,
          strings.meanRank,
        ]}
        rows={names.map((name, j) => ({
          key: `${j}-${name}`,
          cells: [name, n(r.medians[j]), n(r.rankSums[j]), n(r.meanRanks[j])],
        }))}
      />
      <h3 className={WIDGET_H3}>{strings.posthocHeading}</h3>
      <p className={HINT}>
        {t(strings.posthocHint, {
          df: fmt(r.conoverDf),
          m: fmt(r.pairs.length),
        })}
      </p>
      <ResultTable
        head={[
          strings.comparison,
          strings.rankDifference,
          strings.q,
          strings.pNemenyi,
          strings.tStat,
          strings.pConover,
          strings.pConoverHolm,
        ]}
        rows={r.pairs.map((pair) => ({
          key: `${pair.first}-${pair.second}`,
          cells: [
            t(strings.versus, { a: names[pair.first], b: names[pair.second] }),
            n(pair.rankSumDifference),
            n(pair.q),
            formatRangeP(pair.pNemenyi, fmt, strings.pBelow),
            n(pair.t),
            formatP(pair.pConover, fmt),
            formatP(pair.pConoverHolm, fmt),
          ],
        }))}
      />
      <h3 className={WIDGET_H3}>{strings.ranksHeading}</h3>
      <p className={HINT}>
        {r.n > RANK_TABLE_LIMIT ? strings.ranksHidden : strings.ranksHint}
      </p>
      {r.n > RANK_TABLE_LIMIT ? null : (
        <ResultTable
          head={[strings.subject, ...names]}
          rows={r.ranks.map((row, i) => ({
            key: `subject-${i + 1}`,
            cells: [fmt(i + 1), ...row.map((rank) => n(rank))],
          }))}
        />
      )}
      <Working heading={strings.howCalculated}>
        <p>
          χ²′ = 12 / (n k (k + 1)) × Σ Rⱼ² − 3 n (k + 1) = 12 / ({fmt(r.n)} ×{" "}
          {fmt(r.k)} × {fmt(r.k + 1)}) × ({terms}) − 3 × {fmt(r.n)} ×{" "}
          {fmt(r.k + 1)} = {n(r.chiSquareUncorrected)}
        </p>
        <p>
          C = 1 − Σ(t³ − t) / (n k (k² − 1)) = 1 − {fmt(r.tieTerm)} /{" "}
          {fmt(r.n * r.k * (r.k * r.k - 1))} = {n(r.tieCorrection)}
        </p>
        <p>
          χ²F = χ²′ / C = {n(r.chiSquareUncorrected)} / {n(r.tieCorrection)} ={" "}
          {n(r.chiSquare)}; df = k − 1 = {fmt(r.df)}; p = P(χ²({fmt(r.df)}) ≥{" "}
          {n(r.chiSquare)}) = {formatP(r.p, fmt)}
        </p>
        <p>
          W = χ²F / (n (k − 1)) = {n(r.chiSquare)} / ({fmt(r.n)} × {fmt(r.df)})
          = {n(r.kendallW)}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
